import { PermissionType } from './permissions.constants';

/**
 * Map route path to permissions (user needs ANY of these to see the menu item / access the route).
 * Empty array or missing entry = visible to all authenticated users.
 * Use Permission constants from permissions.constants.
 */
export const ROUTE_PERMISSIONS: Record<string, PermissionType[]> = {
  // Dashboard - all
  '/dashboard': [],

  // Daily Roster - front desk, nurse, doctor, biller
  '/daily-roaster': ['daily_roster.view'],
  '/daily-roaster/check-in-patient': ['daily_roster.view'],
  '/daily-roaster/co-pay-details': ['daily_roster.view'],
  '/daily-roaster/appointment-history': ['daily_roster.view'],
  '/daily-roaster/open-statement': ['daily_roster.view'],
  '/daily-roaster/new-charge-entry': ['daily_roster.view'],
  '/daily-roaster/add-plan': ['daily_roster.view'],
  '/daily-roaster/payment': ['batch.any'],
  '/daily-roaster/plan-follow-up': ['follow_up.view'],

  // Patient
  '/patient': [
    'patient.search',
    'patient.register',
    'patient.edit',
    'patient.insurance_plan',
    'patient.documents',
    'patient.today_clinical_worklist',
    'patient.chart_limited',
    'patient.doctor_worklist',
    'patient.chart_full',
    'patient.financial_summary',
    'patient.charge_history',
  ],
  '/patient/patient-list': ['patient.search'],
  '/patient/add': ['patient.register'],
  '/patient/edit': ['patient.edit'],
  '/patient/details': ['patient.search', 'patient.edit', 'patient.chart_limited', 'patient.chart_full', 'patient.financial_summary'],
  '/patient/lab-order': ['patient.chart_limited', 'patient.chart_full'],
  '/patient/test-results': ['patient.chart_limited', 'patient.chart_full'],
  '/patient/sonogram-list': ['patient.chart_limited', 'patient.chart_full'],
  '/patient/sonogram-view': ['patient.chart_limited', 'patient.chart_full'],
  '/patient/visit-details': ['patient.search', 'patient.chart_limited', 'patient.chart_full', 'patient.charge_history'],
  '/patient/visit-details-list': ['patient.search', 'patient.chart_limited', 'patient.chart_full', 'patient.charge_history'],
  '/patient/patient-summary': ['patient.financial_summary', 'patient.chart_limited', 'patient.chart_full'],
  '/patient/extra-info': ['patient.edit', 'patient.chart_limited', 'patient.chart_full'],
  '/patient/consent-procedure': ['patient.edit', 'patient.documents'],
  '/patient/visit-charge': ['patient.charge_history', 'patient.financial_summary'],

  // Scheduler
  '/scheduler': [
    'scheduler.appointment_calendar',
    'scheduler.checkin_checkout',
    'scheduler.quick_schedule',
    'scheduler.wait_list',
    'scheduler.clinical_schedule',
    'scheduler.provider_schedule',
    'scheduler.overbook_squeeze',
    'scheduler.billing_view',
  ],
  // Allow Schedule/Create menu items for Doctor (provider_schedule) and Nurse (clinical_schedule).
  '/scheduler/schedule': ['scheduler.appointment_calendar', 'scheduler.quick_schedule', 'scheduler.provider_schedule', 'scheduler.clinical_schedule'],
  '/scheduler/create': ['scheduler.appointment_calendar', 'scheduler.provider_schedule', 'scheduler.clinical_schedule'],
  '/scheduler/new-schedule': ['scheduler.appointment_calendar'],
  '/scheduler/provider-schedule': ['scheduler.provider_schedule', 'scheduler.clinical_schedule'],
  '/scheduler/new-appointment': ['scheduler.appointment_calendar'],

  // Utility - any utility permission
  '/utility': [
    'utility.location_provider_lookup',
    'utility.front_desk_shortcuts',
    'utility.label_receipt_printing',
    'utility.nursing_tools',
    'utility.task_list',
    'utility.clinical_templates',
    'utility.prescription_tools',
    'utility.batch_management',
    'utility.code_fee_tools',
  ],

  // Master
  '/master': ['master.any', 'master.doctor_menu'],
  '/master/patient-messages': ['master.any', 'master.doctor_menu'],
  '/master/to-do-list': ['master.any', 'master.doctor_menu'],
  '/master/show-system-options': ['master.any', 'master.doctor_menu'],
  '/master/appointment-status-color': ['master.any', 'master.doctor_menu'],

  // Batch - biller
  '/batch': ['batch.any'],
  '/batch/charge-batchadmin': ['batch.any'],
  '/batch/payment-batchadmin': ['batch.any'],
  '/batch/payment-management': ['batch.any'],
  '/batch/denial-batchadmin': ['batch.any'],
  '/batch/charge-entry': ['batch.any'],
  '/batch/charge-listing': ['batch.any'],
  '/batch/paper-submit': ['batch.any'],
  '/batch/batch-submit-log': ['batch.any'],
  '/batch/batch-submit-visit-summary': ['batch.any'],
  '/batch/electronic-submit': ['batch.any'],
  '/batch/claim-editing': ['batch.any'],
  '/batch/edi-report-log': ['batch.any'],
  '/batch/ticket-charge-entry': ['batch.any'],
  '/batch/era-file-process': ['batch.any'],
  '/batch/statement-printing': ['batch.any'],
  '/batch/wcnf-printing': ['batch.any'],
  '/batch/payment-document-viewer': ['batch.any'],

  // User
  '/user': [],
  '/user/profile': [],

  // Follow Up
  '/follow-up': ['follow_up.view'],
  '/follow-up/plan-follow-up': ['follow_up.view'],
  '/follow-up/patient-follow-up': ['follow_up.view'],

  // Reports - any report permission
  '/reports': [
    'reports.checkin_summary',
    'reports.noshow_cancellation',
    'reports.daily_copay_collection',
    'reports.followup',
    'reports.procedure_day_sheet',
    'reports.patient_didnot_showup',
  ],
  '/reports/patient-reports': ['reports.checkin_summary', 'reports.daily_copay_collection', 'patient.search'],
  '/reports/patient-reports/patients-list': ['patient.search', 'reports.checkin_summary'],
  '/reports/patient-reports/patient-co-pay': ['reports.daily_copay_collection'],
  '/reports/management-reports': ['batch.any'],
  '/reports/schedule-reports': ['scheduler.appointment_calendar', 'scheduler.clinical_schedule', 'scheduler.provider_schedule'],
  '/reports/plan-reports': ['batch.any'],
  '/reports/plan-reports/plan-outstanding-balance': ['batch.any'],
  '/reports/plan-reports/payment-ledger': ['batch.any'],
  '/reports/plan-reports/follow-up-bucket-detail': ['batch.any'],
  '/reports/plan-reports/diagnosis-procedure/by-plan': ['batch.any'],
  '/reports/plan-reports/diagnosis-procedure/by-patient': ['batch.any'],
  '/reports/plan-reports/diagnosis-procedure/visitwise-payment-report': ['batch.any'],
  '/reports/procedure-day-sheet': ['reports.procedure_day_sheet'],
  '/reports/patient-didnot-showup': ['reports.patient_didnot_showup', 'reports.noshow_cancellation'],
  '/reports/follow-up-reports': ['reports.followup'],
  '/reports/other-reports': ['batch.any'],
  '/reports/audit-report': ['batch.any'],
  '/reports/mdvip-patient-data-export': ['reports.followup'],
};

/**
 * Get permissions required for a route (exact match, then parent path fallback).
 * Returns empty array if no restriction (all authenticated).
 */
export function getPermissionsForRoute(routePath: string): PermissionType[] {
  const normalized = routePath.replace(/\/$/, '') || '/';
  const exact = ROUTE_PERMISSIONS[normalized];
  if (exact) return exact;
  const parts = normalized.split('/').filter(Boolean);
  for (let i = parts.length - 1; i >= 1; i--) {
    const parent = '/' + parts.slice(0, i).join('/');
    const perms = ROUTE_PERMISSIONS[parent];
    if (perms) return perms;
  }
  return ROUTE_PERMISSIONS['/'] ?? [];
}
